import { css } from '@emotion/css';
import { texts } from '../i18n';
import { TextField } from './TextField';
import { Colors } from './vars';
import { ButtonContainer, TextButton } from './TextButton';
import { useState } from 'react';
import { Language } from 'dg-web-shared/lib/Localized.ts';

export function ChangePasswordForm({ onCancel, onSave, language }: Props) {
    const [password, setPassword] = useState<string>('');
    const [passwordEdited, setPasswordEdited] = useState<boolean>(false);
    const [confirmation, setConfirmation] = useState<string>('');

    function handleEnter(): void {
        if (validate(password, confirmation) === ValidationResult.OK) {
            onSave(password);
        }
    }

    function passwordErrorText(): string {
        if (!passwordEdited) {
            return '';
        }

        switch (validate(password, confirmation)) {
            case ValidationResult.ERROR_EMPTY:
                return texts[language].requiredField;

            case ValidationResult.ERROR_TOO_SHORT:
                return texts[language].passwordIsTooShort(MIN_PASSWORD_LENGTH);

            default:
                return '';
        }
    }

    function confirmationErrorText(): string {
        if (!passwordEdited || confirmation.length < password.length) {
            return '';
        }

        if (
            validate(password, confirmation) ===
            ValidationResult.ERROR_CONFIRMATION_MISMATCH
        ) {
            return texts[language].passwordFieldsDoNotMatch;
        }

        return '';
    }

    return (
        <div className={style}>
            <div>
                <TextField
                    data-test="password"
                    inputType="password"
                    label={texts[language].newPassword}
                    value={password}
                    onChange={(newPassword: string) => setPassword(newPassword)}
                    onBlur={() => setPasswordEdited(true)}
                    onEnter={handleEnter}
                    errorText={passwordErrorText()}
                />
            </div>
            <div>
                <TextField
                    data-test="confirmation"
                    inputType="password"
                    label={texts[language].confirmPassword}
                    value={confirmation}
                    onChange={(newConfirmation: string) =>
                        setConfirmation(newConfirmation)
                    }
                    onEnter={handleEnter}
                    errorText={confirmationErrorText()}
                />
            </div>

            <div className={css({ marginTop: '36px' })}>
                <ButtonContainer>
                    <TextButton
                        data-test="cancel"
                        onClick={onCancel}
                        label={texts[language].cancel}
                    />

                    <TextButton
                        data-test="save"
                        onClick={() => onSave(password)}
                        label={texts[language].save}
                        disabled={
                            validate(password, confirmation) !==
                            ValidationResult.OK
                        }
                    />
                </ButtonContainer>
            </div>
        </div>
    );
}

export interface Props {
    onCancel: () => void;
    onSave: (newPassword: string) => void;
    language: Language;
}

const style = css`
    margin: 24px 10px;
    padding: 0px 40px;
    & [data-error='true'] {
        color: ${Colors.red};
    }
`;

export function validate(
    password: string,
    confirmation: string,
): ValidationResult {
    if (password === '') {
        return ValidationResult.ERROR_EMPTY;
    }

    if (password.length < MIN_PASSWORD_LENGTH) {
        return ValidationResult.ERROR_TOO_SHORT;
    }

    if (password !== confirmation) {
        return ValidationResult.ERROR_CONFIRMATION_MISMATCH;
    }

    return ValidationResult.OK;
}

export enum ValidationResult {
    OK,
    ERROR_EMPTY,
    ERROR_TOO_SHORT,
    ERROR_CONFIRMATION_MISMATCH,
}

export const MIN_PASSWORD_LENGTH = 5;
